import React, { useState } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import { RestorationPhase } from '../../types/teardown';
import { BOMItem } from '../../types/bom';
import { BudgetVarianceCard } from './BudgetVarianceCard';
import { PartsMatrixTable } from './PartsMatrixTable';
import { HollanderLookupBar } from './HollanderLookupBar';
import { AddPartModal } from './AddPartModal';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Layers,
  BookOpen,
  Plus,
  Download,
  Package,
} from 'lucide-react';

export const BOMView: React.FC = () => {
  const { state } = useRestoMod();
  const { project, bomItems } = state;

  const [activeSubView, setActiveSubView] = useState<'matrix' | 'hollander'>('matrix');
  const [selectedPhase, setSelectedPhase] = useState<RestorationPhase | 'ALL'>('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const budgetCap = project?.budgetCap || 65000;

  // CSV Export utility
  const handleExportCSV = () => {
    const headers = [
      'ID',
      'Part Description',
      'Part Number',
      'Phase',
      'System',
      'Condition',
      'Status',
      'Vendor',
      'Quantity',
      'Unit',
      'Estimated Cost',
      'Actual Cost',
      'Shipping Cost',
      'Tax Cost',
      'Core Charge',
      'Core Refunded',
      'Hollander Number',
      'Receipt Number',
      'Notes',
    ];

    const rows = bomItems.map((item: BOMItem) => [
      `"${item.id}"`,
      `"${item.partName.replace(/"/g, '""')}"`,
      `"${item.partNumber.replace(/"/g, '""')}"`,
      `"${item.phase}"`,
      `"${item.system}"`,
      `"${item.condition}"`,
      `"${item.status}"`,
      `"${item.vendor.replace(/"/g, '""')}"`,
      item.quantity,
      `"${item.unit}"`,
      item.estimatedCost.toFixed(2),
      item.actualCost.toFixed(2),
      item.shippingCost.toFixed(2),
      item.taxCost.toFixed(2),
      item.coreCharge.toFixed(2),
      item.coreRefunded.toFixed(2),
      `"${item.hollanderNumber || ''}"`,
      `"${item.receiptNumber || ''}"`,
      `"${item.notes.replace(/"/g, '""')}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `BOM_${project?.title?.replace(/\s+/g, '_') || 'RestoMod'}_Ledger.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const installedCount = bomItems.filter((i) => i.status === 'Installed').length;

  return (
    <div className="space-y-6">
      {/* BOM View Header & Subview Navigation */}
      <div className="bg-shop-darkest border border-shop-border rounded p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Left: Title & Vehicle Context */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-shop-panel rounded border border-shop-border text-safety-amber shadow-machined shrink-0">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-bold text-shop-highlight uppercase tracking-wider">
                Bill of Materials &amp; Parts Procurement Matrix
              </h2>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-shop-panel border border-shop-border text-safety-amber">
                R4 SPEC COMPLIANT
              </span>
            </div>
            <p className="text-xs text-shop-muted mt-0.5">
              Target Build: <strong className="text-shop-light">{project?.title || '1969 Chevrolet Camaro SS'}</strong> |
              Allocated Cap: <strong className="text-shop-light">${budgetCap.toLocaleString()}</strong> |
              Installed Progress:{' '}
              <strong className="text-safety-green">
                {bomItems.length > 0 ? ((installedCount / bomItems.length) * 100).toFixed(0) : 0}%
              </strong>
            </p>
          </div>
        </div>

        {/* Right: Quick Actions & View Switcher */}
        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto justify-end">
          {/* Subview Toggle Switch */}
          <div className="inline-flex p-1 bg-shop-panel border border-shop-border rounded">
            <button
              onClick={() => setActiveSubView('matrix')}
              className={`min-h-[44px] px-3 py-1.5 rounded text-xs font-mono font-bold uppercase transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubView === 'matrix'
                  ? 'bg-safety-amber text-shop-darkest shadow-sm'
                  : 'text-shop-muted hover:text-shop-highlight'
              }`}
            >
              <Package className="w-3.5 h-3.5" />
              <span>Parts Matrix</span>
            </button>

            <button
              onClick={() => setActiveSubView('hollander')}
              className={`min-h-[44px] px-3 py-1.5 rounded text-xs font-mono font-bold uppercase transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubView === 'hollander'
                  ? 'bg-safety-amber text-shop-darkest shadow-sm'
                  : 'text-shop-muted hover:text-shop-highlight'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Hollander Interchange</span>
            </button>
          </div>

          {/* Export CSV Button */}
          <MechanicalButton
            variant="outline"
            size="default"
            icon={<Download className="w-4 h-4 text-blueprint-cyan" />}
            onClick={handleExportCSV}
            title="Export complete Bill of Materials as CSV"
          >
            Export CSV
          </MechanicalButton>

          {/* Add Part Button */}
          <MechanicalButton
            variant="primary"
            size="default"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Component
          </MechanicalButton>
        </div>
      </div>

      {/* Main View Area */}
      {activeSubView === 'matrix' ? (
        <div className="space-y-6">
          {/* 1. Real-Time Budget Variance Card with Phase Breakdowns */}
          <BudgetVarianceCard
            selectedPhase={selectedPhase}
            onSelectPhase={(phase) => setSelectedPhase(phase)}
          />

          {/* 2. Parts Matrix Table */}
          <PartsMatrixTable initialPhaseFilter={selectedPhase} />
        </div>
      ) : (
        /* Hollander Interchange Salvage Explorer View */
        <div className="bg-shop-panel border border-shop-border rounded p-5 shadow-tactile">
          <div className="border-b border-shop-border pb-3 mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-shop-highlight uppercase tracking-wider flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-safety-amber" />
                Royal Hollander Classic Car Salvage Interchange Directory
              </h3>
              <p className="text-xs text-shop-muted">
                Official scrap yard and parts recycler cross-referencing index for classic muscle and vintage donor cars
              </p>
            </div>
            <button
              type="button"
              onClick={() => setActiveSubView('matrix')}
              className="min-h-[44px] px-3 inline-flex items-center text-xs font-mono text-safety-amber underline hover:text-safety-amberDark cursor-pointer"
            >
              Back to Parts Matrix
            </button>
          </div>

          <HollanderLookupBar inline={true} />
        </div>
      )}

      {/* Add Part Modal */}
      {isAddModalOpen && (
        <AddPartModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          initialPhase={selectedPhase !== 'ALL' ? selectedPhase : undefined}
        />
      )}
    </div>
  );
};

export default BOMView;
